import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, Lock, ArrowRight, Check, ChevronDown } from "lucide-react";

export function ProgramGate({ productKey, priceLabel, purchaseCta = "Unlock" }) {
  return (
    <div className="pt-20 sm:pt-24 min-h-screen aurora grain flex items-center" data-testid={`program-locked-${productKey}`}>
      <div className="mx-auto max-w-lg px-5 sm:px-6 py-16 text-center">
        <Lock className="h-8 w-8 text-accent mx-auto mb-6" />
        <div className="eyebrow mb-3 text-accent">Locked</div>
        <h1 className="font-serif text-[2.25rem] leading-[1.05] sm:text-4xl mb-4">
          This chapter is waiting for you.
        </h1>
        <p className="text-muted-foreground mb-8 leading-relaxed">
          Unlock <span className="font-serif italic text-foreground">{priceLabel}</span> to enter this program.
        </p>
        <Link to="/membership" className="block sm:inline-block">
          <Button className="w-full sm:w-auto btn-pill font-mono uppercase text-xs tracking-widest h-12 px-6 gap-2">
            {purchaseCta} <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

export function useProgram(url) {
  const [state, setState] = useState({ loading: true, data: null, locked: false, needsAuth: false });
  const { user } = useAuth();
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setState({ loading: false, data: null, locked: false, needsAuth: true });
      return;
    }
    api
      .get(url)
      .then((r) => {
        if (!cancelled) setState({ loading: false, data: r.data, locked: false, needsAuth: false });
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 402) {
          setState({ loading: false, data: null, locked: true, needsAuth: false });
        } else if (e?.response?.status === 401) {
          setState({ loading: false, data: null, locked: false, needsAuth: true });
        } else {
          setState({ loading: false, data: null, locked: false, needsAuth: false });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [url, user]);
  return state;
}

export function ProgramLoading() {
  return (
    <div className="pt-24 min-h-screen flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-accent" />
    </div>
  );
}

export function NeedAuth() {
  return (
    <div className="pt-20 sm:pt-24 min-h-screen aurora grain flex items-center">
      <div className="mx-auto max-w-lg px-5 sm:px-6 py-16 text-center">
        <div className="eyebrow mb-3 text-accent">Sign in required</div>
        <h1 className="font-serif text-[2.25rem] leading-[1.05] sm:text-4xl mb-4">
          Sign in to enter your programs.
        </h1>
        <p className="text-muted-foreground mb-8 leading-relaxed">
          Your purchases are tied to your account. Log in with the email you used to pay.
        </p>
        <Link to="/auth">
          <Button className="btn-pill font-mono uppercase text-xs tracking-widest h-12 px-6">
            Sign in
          </Button>
        </Link>
      </div>
    </div>
  );
}

export function ProgramShell({ title, eyebrow, children }) {
  return (
    <div className="pt-20 sm:pt-24" data-testid="program-page">
      <section className="mx-auto max-w-5xl px-5 sm:px-6 py-10 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="eyebrow mb-4 text-accent">{eyebrow}</div>
          <h1 className="font-serif text-[2.5rem] leading-[1.05] sm:text-6xl mb-8 sm:mb-12">{title}</h1>
          {children}
        </motion.div>
      </section>
    </div>
  );
}

export function useProgress(programKey) {
  const { user } = useAuth();
  const storageKey = `shift-progress:${user?.id || "anon"}:${programKey}`;
  const [done, setDone] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || "[]");
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      setDone(JSON.parse(localStorage.getItem(storageKey) || "[]"));
    } catch {
      setDone([]);
    }
  }, [storageKey]);
  const toggle = (id) => {
    setDone((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };
  return { done, toggle, isDone: (id) => done.includes(id) };
}

export function ProgressBar({ done, total, label = "complete" }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="mb-8 sm:mb-10" data-testid="program-progress">
      <div className="flex justify-between font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-2">
        <span data-testid="program-progress-count">{done} / {total} {label}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-px bg-border relative">
        <div className="absolute inset-y-0 left-0 bg-accent transition-[width] duration-500" style={{ width: `${pct}%`, height: 2, top: -0.5 }} />
      </div>
    </div>
  );
}

export function CompleteButton({ done, onClick, testId }) {
  return (
    <Button
      variant={done ? "default" : "outline"}
      onClick={onClick}
      data-testid={testId}
      className="btn-pill font-mono uppercase text-[10px] tracking-widest h-10 px-5 gap-2"
    >
      <Check className="h-3.5 w-3.5" /> {done ? "Completed" : "Mark complete"}
    </Button>
  );
}

export function Block({ label, children, accent = false }) {
  return (
    <div className={accent ? "border-l-2 border-accent pl-4 sm:pl-5" : ""}>
      <div className="eyebrow mb-2">{label}</div>
      {children}
    </div>
  );
}

export function Paragraphs({ text, className = "font-serif text-base sm:text-lg leading-relaxed" }) {
  return (
    <div className="space-y-4">
      {String(text).split(/\n\s*\n/).map((p, i) => (
        <p key={i} className={className}>{p}</p>
      ))}
    </div>
  );
}

export function PromptList({ items, numbered = false }) {
  return (
    <ul className="space-y-3">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-3 text-sm sm:text-base leading-relaxed">
          <span className="font-mono text-[10px] tracking-widest text-accent shrink-0 mt-1.5">
            {numbered ? String(i + 1).padStart(2, "0") : "—"}
          </span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

export function Mantra({ text }) {
  return (
    <blockquote className="border border-accent/40 bg-accent/5 p-5 sm:p-6 font-serif italic text-lg sm:text-xl leading-snug">
      “{text}”
    </blockquote>
  );
}

export function AccordionItem({ id, number, eyebrow, title, open, onToggle, done, children }) {
  return (
    <div className={`border ${done ? "border-accent/50" : "border-border"}`} data-testid={`${id}`}>
      <button onClick={onToggle} className="w-full flex items-center justify-between p-5 sm:p-6 text-left gap-4" data-testid={`${id}-toggle`}>
        <div className="flex items-center gap-4 min-w-0">
          <div className="font-mono text-xs tracking-widest text-accent shrink-0 w-6">
            {done ? <Check className="h-4 w-4" /> : number}
          </div>
          <div className="min-w-0">
            {eyebrow && <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">{eyebrow}</div>}
            <h3 className="font-serif text-xl sm:text-2xl leading-snug">{title}</h3>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-6 sm:px-6 sm:pb-8 space-y-7 border-t border-border/40 pt-6">{children}</div>}
    </div>
  );
}
